// Landing page copy and data, kept apart from the layout so it can be checked (landing.test.ts).
// Rules (spec-v2.1 C): no third-party brand names, no testimonials, no stats we can't stand behind.
// Prices: docs/product/cost-analysis.md 5b/5c (proposal), shown as the invite-only beta prices.

export const ASSET = "/landing";

/** The three steps. `img` is the still for steps 1 and 2 (public/landing/); step 3 plays the finished ad. */
export type Step = { n: number; title: string; body: string; img?: string };
export const STEPS: Step[] = [
  { n: 1, title: "Talk to your Director", body: "Say what you want and who it's for. Talk or type.", img: "step-talk.jpg" },
  { n: 2, title: "Approve the plan", body: "See the script and every frame first. Nothing costly runs without your yes.", img: "step-approve.jpg" },
  { n: 3, title: "Get your video", body: "Voices, music and sound, cut for any screen." },
];

export type GalleryAd = { id: string; brand: string; title: string; aspect: "9:16" | "1:1" | "16:9"; seconds: number };
/** The example ads in public/landing/ (src/landing/tools/make-ads.mjs). The brands are made up. */
export const GALLERY: GalleryAd[] = [
  { id: "ad-roast", brand: "Larkspur Bakehouse", title: "Morning roast launch", aspect: "9:16", seconds: 15 },
  { id: "ad-swim", brand: "Ottermill Swim Club", title: "Summer lessons", aspect: "1:1", seconds: 13 },
  { id: "ad-lights", brand: "Nightjar", title: "Ride after dark", aspect: "16:9", seconds: 11 },
  { id: "ad-candles", brand: "Ivy & Ash", title: "Autumn candles", aspect: "9:16", seconds: 13 },
];

// The packs (Creator, Pro and the others) come from the api: pricing.ts.

/** What a typical 30-second video costs, in credits (about a cent each; cost-analysis.md 5b, priced on the ad). */
export const AD_PRICES = [
  { what: "From animated stills", credits: 495 },
  { what: "With four AI video clips", credits: 1890 },
];

export type Faq = { q: string; a: string };
export const FAQ: Faq[] = [
  { q: "What kinds of video can it make?", a: "Ads, animated documentaries, cartoons and short stories, short films and trailers, explainers, faceless Shorts, music videos, photo slideshows, kinetic typography, podcast clips and product demos. More are on the way." },
  { q: "Do I have to talk to it?", a: "No. Type whenever you like, even in the middle of a call. The Director keeps going either way." },
  { q: "What does it cost?", a: "Credits are about a cent each. Talking with the Director is 30 credits a minute (typing to it is 10). It tells you the price before every paid step. Failed work is refunded." },
  { q: "Can I use my own photos?", a: "Yes. Add them to your library and the Director builds scenes from them, so your product looks like your product." },
  { q: "What if I don't like something?", a: "Ask for another take of any frame, clip, music bed or sound, with a note if you like. Every render is kept, so you can always go back." },
  { q: "Will a documentary make things up?", a: "No. It states only the facts in the notes and sources you give it, and can show the source on screen." },
  { q: "What do I get at the end?", a: "An MP4 in 9:16, 1:1 or 16:9 with voices, music, sound effects and captions." },
  { q: "How do I get in?", a: "CAO Studio is in an invite-only beta. Request an invite and we'll email you when there's a spot. Have a code? Sign in to use it." },
];

export const usd = (credits: number) => `$${(credits / 100).toFixed(2)}`;
export const fmt = (n: number) => n.toLocaleString("en-US");

/** Words that must never appear in landing copy: provider and competitor names (house rule: CAO Studio only). */
export const BANNED = [
  "ElevenLabs",
  "Suno",
  "Runway",
  "Veo",
  "Gemini",
  "Google",
  "Seedance",
  "Kling",
  "OpenAI",
  "Remotion",
  "Clerk",
  "Stripe",
  "Auraverse",
];
